<template>
    <div
      ref="headerContainer"
      :class="[$globalStyle.headerContainer, $style.playGameHeaderContainer]"
    >
      <div :class="$style.primaryHeader">
        <template v-if="canUseHostView">
          <HostHeaderBox
            :user="user"
            :joinCode="game.joinCode"
            :allowedPlayers="game.allowedPlayers"
            :readingTime="game.readingTime"
            :discussionTime="game.discussionTime"
            :realTextLabel="gameStore.displayRealTextLabel"
            :saboteur="game.saboteur"
            :gameWinner="gameWinner"
            :openSaboteursModal="openSaboteursModal"
            :openWinnersModal="openWinnersModal"
            :groups="scopedGroups"
            :phase="game.phase"
            :host="game.host"
            :isModerator="isModerator"
          />
          <div
            v-if="canControlPhase &&elementViewPhases.startGameButton.includes(game.phase)"
            :class="[$style.startGameCtaContainer]"
          >
            <HeaderActionButton
              :text="$t('start-game')"
              :iconLink="forwardArrowIcon"
              :clickFunction="startGame"
              backBg="#000000"
              frontBg="#676767"
              textColor="#FFFFFF"
              :rightIcon="true"
              :icon-only="isMobile"
              :button-width="isMobile ? '60px' : isTablet ? '200px' : '230px'"
            />
          </div>
          <div
            v-if="canControlPhase &&elementViewPhases.continueGameButton.includes(game.phase)"
            :class="[$style.continueGameCtaContainer]"
          >
            <HeaderActionButton
              :text="$t('next')"
              :iconLink="forwardArrowIcon"
              :clickFunction="continueGame"
              backBg="#000000"
              frontBg="#676767"
              textColor="#FFFFFF"
              :rightIcon="true"
              :icon-only="isMobile"
              :button-width="isMobile ? '60px' : isTablet ? '140px' : '160px'"
            />
          </div>
          <div
            v-if="canControlPhase && elementViewPhases.endGameButton.includes(game.phase)"
            :class="[$style.endGameCtaContainer]"
          >
            <HeaderActionButton
              :text="$t('end')"
              :iconLink="forwardArrowIcon"
              :clickFunction="endGame"
              backBg="#A50000"
              frontBg="#F77575"
              textColor="#FFFFFF"
              :rightIcon="true"
              :icon-only="isMobile"
              :button-width="isMobile ? '60px' : isTablet ? '140px' : '160px'"
            />
          </div>
        </template>
        <template v-if="!canUseHostView">
          <PlayerHeaderBox
            :user="user"
            :allowedPlayers="game.allowedPlayers"
            :playersJoined="game.players?.length ?? 0"
            :host="game.host"
            :joinCode="Number(game.joinCode)"
            :realTextLabel="gameStore.displayRealTextLabel"
            :isSaboteur="isSaboteur"
            :saboteur="game.saboteur"
            :gameWinner="gameWinner"
            :playerGroup="myGroup"
            :showRoleButton="playerRoleReady"
            :openRoleModal="openPlayerRoleModal"
          />
        </template>
      </div>

      <div :class="$style.secondHeader">
        <template v-if="game.phase == 'JOIN'">
          <div :class="$style.joinCodeBoxContainer">
            <JoinCodeBox :joinCode="game.joinCode" />
          </div>
        </template>
        <template v-if="game.phase != 'JOIN'">
          <template v-if="canUseHostView">
            <template v-if="isMobile">
              <div :class="$style.mobileSecondHeader">
                <div :class="$style.mobileSecondHeaderTop">
                  <template
                    v-if="
                      elementViewPhases.focusGroupBox.includes(game.phase)
                    "
                  >
                    <div :class="$style.focusGroupBoxContainer">
                      <FocusGroupBox :groups="displayGroupsWithPlayers" />
                    </div>
                  </template>
                  <template
                    v-if="
                      elementViewPhases.playersCountBox.includes(game.phase)
                    "
                  >
                    <div :class="$style.playersCountBoxContainer">
                      <PlayersCountBox
                        :allowedPlayers="game.allowedPlayers"
                        :players="game.players ?? []"
                        :groupLimit="game.groupLimit"
                        :groups="scopedGroupsWithPlayers"
                        :phase="game.phase"
                        :isModerator="isModerator"
                        :moderatorGroupIds="moderatorGroupIds"
                        :hostId="game.host?.id"
                        :pendingModerators="pendingModerators"
                      />
                    </div>
                  </template>
                </div>
                <div :class="$style.mobileSecondHeaderBottom">
                  <div :class="$style.phaseTimerBoxContainer">
                    <PhaseTimerBox
                      @timerEnded="onTimerEnd"
                      :phase="game.phase"
                      :readingTime="game.readingTime"
                      :discussionTime="game.discussionTime"
                      :phaseStartedAt="game.phaseStartedAt"
                      :phaseEndsAt="game.phaseEndsAt"
                      :phaseDurationSeconds="game.phaseDurationSeconds"
                      :nowMs="timerNowMs"
                    />
                  </div>
                </div>
              </div>
            </template>
            <template v-else>
              <template
                v-if="elementViewPhases.focusGroupBox.includes(game.phase)"
              >
                <div :class="$style.focusGroupBoxContainer">
                  <FocusGroupBox :groups="displayGroupsWithPlayers" />
                </div>
              </template>
              <div :class="$style.phaseTimerBoxContainer">
                <PhaseTimerBox
                  @timerEnded="onTimerEnd"
                  :phase="game.phase"
                  :readingTime="game.readingTime"
                  :discussionTime="game.discussionTime"
                  :phaseStartedAt="game.phaseStartedAt"
                  :phaseEndsAt="game.phaseEndsAt"
                  :phaseDurationSeconds="game.phaseDurationSeconds"
                  :nowMs="timerNowMs"
                />
              </div>
              <template
                v-if="elementViewPhases.playersCountBox.includes(game.phase)"
              >
                <div :class="$style.playersCountBoxContainer">
                  <PlayersCountBox
                    :allowedPlayers="game.allowedPlayers"
                    :players="game.players ?? []"
                    :groupLimit="game.groupLimit"
                    :groups="scopedGroupsWithPlayers"
                    :isModerator="isModerator"
                    :moderatorGroupIds="moderatorGroupIds"
                    :hostId="game.host?.id"
                    :pendingModerators="pendingModerators"
                  />
                </div>
              </template>
            </template>
          </template>

          <template v-if="!canUseHostView">
            <div :class="$style.phaseTimerBoxContainer">
              <PhaseTimerBox
                @timerEnded="onTimerEnd"
                :phase="game.phase"
                :readingTime="game.readingTime"
                :discussionTime="game.discussionTime"
                :phaseStartedAt="game.phaseStartedAt"
                :phaseEndsAt="game.phaseEndsAt"
                :phaseDurationSeconds="game.phaseDurationSeconds"
                :nowMs="timerNowMs"
              />
            </div>
          </template>
        </template>
      </div>
    </div>
    <div
      ref="tabsContainer"
      :class="$style.tabsContainer"
      v-if="
        elementViewPhases.gameTextsTabButton.includes(game.phase) ||
        elementViewPhases.prelimVotesDisplayTabButton.includes(game.phase) ||
        elementViewPhases.leaderboardTabButton.includes(game.phase) ||
        elementViewPhases.textVotesTabButton.includes(game.phase) ||
        elementViewPhases.saboteurVotesTabButton.includes(game.phase)
      "
    >
      <div
        :class="$style.tabButton"
        v-if="elementViewPhases.gameTextsVoteTabButton.includes(game.phase)"
      >
        <TabSelectionButton
          :text="$t('text-votes')"
          :iconLink="
            tab == 'gameTextsVote'
              ? gameTextsVoteWhiteIcon
              : gameTextsVoteBlackIcon
          "
          :clickFunction="() => selectTab('gameTextsVote')"
          backBg="#000000"
          :frontBg="tab == 'gameTextsVote' ? '#676767' : '#E9E9E9'"
          :textColor="tab == 'gameTextsVote' ? '#FFFFFF' : '#000000'"
          buttonWidth="100%"
        />
      </div>
      <div
        :class="$style.tabButton"
        v-if="elementViewPhases.gameTextsTabButton.includes(game.phase)"
      >
        <TabSelectionButton
          :text="$t('game-texts')"
          :iconLink="
            tab == 'gameTexts'
              ? gameTextsWhiteIcon
              : gameTextsBlackIcon
          "
          :clickFunction="() => selectTab('gameTexts')"
          backBg="#000000"
          :frontBg="tab == 'gameTexts' ? '#676767' : '#E9E9E9'"
          :textColor="tab == 'gameTexts' ? '#FFFFFF' : '#000000'"
          buttonWidth="100%"
        />
      </div>
      <div
        :class="$style.tabButton"
        v-if="
          elementViewPhases.prelimVotesDisplayTabButton.includes(game.phase)
        "
      >
        <TabSelectionButton
          :text="$t('prelim-votes')"
          :iconLink="
            tab == 'prelimVotes' ? votesWhiteIcon : votesBlackIcon
          "
          :clickFunction="() => selectTab('prelimVotes')"
          backBg="#000000"
          :frontBg="tab == 'prelimVotes' ? '#676767' : '#E9E9E9'"
          :textColor="tab == 'prelimVotes' ? '#FFFFFF' : '#000000'"
          buttonWidth="100%"
        />
      </div>
      <div
        :class="$style.tabButton"
        v-if="elementViewPhases.leaderboardTabButton.includes(game.phase)"
      >
        <TabSelectionButton
          :text="$t('leaderboard')"
          :iconLink="
            tab == 'leaderboard'
              ? leaderboardWhiteIcon
              : leaderboardBlackIcon
          "
          :clickFunction="() => selectTab('leaderboard')"
          backBg="#000000"
          :frontBg="tab == 'leaderboard' ? '#676767' : '#E9E9E9'"
          :textColor="tab == 'leaderboard' ? '#FFFFFF' : '#000000'"
          buttonWidth="100%"
        />
      </div>
      <div
        :class="$style.tabButton"
        v-if="elementViewPhases.textVotesTabButton.includes(game.phase)"
      >
        <TabSelectionButton
          :text="$t('text-votes')"
          :iconLink="
            tab == 'textVotes' ? votesWhiteIcon : votesBlackIcon
          "
          :clickFunction="() => selectTab('textVotes')"
          backBg="#000000"
          :frontBg="tab == 'textVotes' ? '#676767' : '#E9E9E9'"
          :textColor="tab == 'textVotes' ? '#FFFFFF' : '#000000'"
          buttonWidth="100%"
        />
      </div>
      <div
        :class="$style.tabButton"
        v-if="elementViewPhases.saboteurVotesTabButton.includes(game.phase)"
      >
        <TabSelectionButton
          :text="$t('saboteur-votes')"
          :iconLink="
            tab == 'saboteurVotes'
              ? saboteurVotesWhiteIcon
              : saboteurVotesBlackIcon
          "
          :clickFunction="() => selectTab('saboteurVotes')"
          backBg="#000000"
          :frontBg="tab == 'saboteurVotes' ? '#676767' : '#E9E9E9'"
          :textColor="tab == 'saboteurVotes' ? '#FFFFFF' : '#000000'"
          buttonWidth="100%"
        />
      </div>
    </div>
    <div ref="bodyContainer" :class="$style.bodyContainer">
      <template
        v-if="
          (canUseHostView &&
            elementViewPhases.hostGameTextsBox.includes(game.phase)) ||
          (!canUseHostView &&
            elementViewPhases.playerGameTextsBox.includes(game.phase))
        "
      >
        <template
          v-if="
            ((game.phase == 'DISCUSS' || game.phase == 'DEBRIEF') &&
              tab == 'gameTexts') ||
            (game.phase != 'DISCUSS' && game.phase != 'DEBRIEF')
          "
        >
          <transition name="fade" mode="out-in">
            <div
              v-if="showEnlargedGameTexts"
              :class="$style.enlargedTextsContainer"
            >
              <EnlargedGameTextsBox
                :texts="game.texts ?? []"
                :discipline="game.discipline"
                :codeLanguage="game.codeLanguage"
                :textTitle="game.textTitle"
                :realTextId="gameStore.displayRealTextId"
                :realTextLabel="gameStore.displayRealTextLabel"
                :closeEnlargedTextsBox="closeEnlargedGameTexts"
              />
            </div>
          </transition>
        </template>
      </template>

      <template
        v-if="
          (canUseHostView &&
            elementViewPhases.hostGameChatBox.includes(game.phase)) ||
          (!canUseHostView &&
            elementViewPhases.playerGameChatBox.includes(game.phase))
        "
      >
        <transition name="fade" mode="out-in">
          <div
            v-if="showAllGroupsChats"
            :class="$style.allGroupsChatsContainer"
          >
            <AllGroupsChatsBox
              :closeAllGroupsChats="closeAllGroupsChats"
              :groups="scopedGroupsWithMessages"
              :user="user"
              :sendMessage="sendMessage"
              :readOnly="chatReadOnly"
            />
          </div>
        </transition>
      </template>

      <template
        v-if="elementViewPhases.prelimVotesDisplayBox.includes(game.phase)"
      >
        <template
          v-if="
            (game.phase == 'DISCUSS' && tab == 'prelimVotes') ||
            game.phase != 'DISCUSS'
          "
        >
          <transition name="fade" mode="out-in">
            <div
              v-if="showAllGroupsPrelimVotes"
              :class="$style.allGroupsPrelimVotesContainer"
            >
              <AllGroupsPrelimVotesBox
                :closeAllGroupsPrelimVotes="closeAllGroupsPrelimVotes"
                :groups="scopedGroupsWithPrelimVotes"
                :isHost="isHost"
                :texts="game.texts ?? []"
                :user="user"
              />
            </div>
          </transition>
        </template>
      </template>

      <template
        v-if="elementViewPhases.gameTextsVoteBox.includes(game.phase)"
      >
        <template v-if="tab == 'gameTextsVote'">
          <transition name="fade" mode="out-in">
            <div
              v-if="showAllGroupsGameTextsVotes"
              :class="$style.allGroupsGameTextsVotesContainer"
            >
              <AllGroupsGameTextsVotesBox
                :closeAllGroupsGameTextsVotes="closeAllGroupsGameTextsVotes"
                :groups="scopedGroupsWithTextVotes"
                :isHost="isHost"
                :user="user"
              />
            </div>
          </transition>
        </template>
      </template>

      <template v-if="elementViewPhases.textVotesBox.includes(game.phase)">
        <template v-if="tab == 'textVotes'">
          <div
            v-if="showAllGroupsResultTextsVotes"
            :class="$style.allGroupsResultTextsVotesContainer"
          >
            <AllGroupsResultTextsVotesBox
              :closeAllGroupsResultTextsVotes="closeAllGroupsResultTextsVotes"
              :groups="scopedGroupsWithTextVotes"
              :isHost="isHost"
              :realTextId="game.realTextId"
            />
          </div>
        </template>
      </template>

      <template v-if="elementViewPhases.leaderboardBox.includes(game.phase)">
        <template v-if="tab == 'leaderboard'">
          <div
            v-if="showAllGroupsLeaderboard && scoresReadyForReveal"
            :class="$style.allGroupsLeaderboardContainer"
          >
            <AllGroupsLeaderboardBox
              :closeAllGroupsLeaderboard="closeAllGroupsLeaderboard"
              :groups="scopedGroupsWithSortedPlayersByScore"
              :isHost="isHost"
            />
          </div>
        </template>
      </template>

      <template
        v-if="elementViewPhases.saboteurVotesBox.includes(game.phase)"
      >
        <template v-if="tab == 'saboteurVotes'">
          <div
            v-if="showAllGroupsSaboteurVotes"
            :class="$style.allGroupsSaboteurVotesContainer"
          >
            <AllGroupsSaboteurVotesBox
              :closeAllGroupsSaboteurVotes="closeAllGroupsSaboteurVotes"
              :groups="scopedGroupsWithSortedSaboteurVotesWithPlayers"
              :isHost="isHost"
            />
          </div>
        </template>
      </template>

      <template
        v-if="elementViewPhases.gamePrelimVoteBox.includes(game.phase) && canAccessCurrentGroupHostFeatures"
      >
        <div
          v-if="showAllGroupsCastPrelimVotes"
          :class="$style.allGroupsCastPrelimVotesContainer"
        >
          <AllGroupsCastVotesBox
            :closeAllGroupsCastVotes="closeAllGroupsCastPrelimVotes"
            :groups="scopedGroupsWithPrelimVoteCastedPlayers"
            :isPrelim="true"
          />
        </div>
      </template>

      <template
        v-if="elementViewPhases.gameVoteBox.includes(game.phase) && canAccessCurrentGroupHostFeatures"
      >
        <div
          v-if="showAllGroupsCastSaboteurVotes"
          :class="$style.allGroupsCastSaboteurVotesContainer"
        >
          <AllGroupsCastVotesBox
            :closeAllGroupsCastVotes="closeAllGroupsCastSaboteurVotes"
            :groups="scopedGroupsWithSaboteurVoteCastedPlayers"
          />
        </div>
      </template>

      <div
        ref="playersContainer"
        v-if="
          (isHost || isModerator) &&
          elementViewPhases.playersBox.includes(game.phase) &&
          !game.phaseLoading 
        "
        :class="$style.playersContainer"
      >
        <PlayersBox
          :players="game.players"
          :groups="displayGroupsWithPlayers"
          :allowedPlayers="game.allowedPlayers"
          :groupLimit="game.groupLimit"
          :phase="game.phase"
          :pendingModerators="pendingModerators"
          :hostId="game.host?.id"
          :isModeratorView="isModerator"
          @update:groups="$emit('update:groups', $event)"
          @update:moderators="$emit('update:pendingModerators', $event)"
        />
      </div>

      <div
        ref="playerLobbyWaitContainer"
        v-if="!canUseHostView && game.phase == 'JOIN'"
        :class="$style.playerLobbyWaitContainer"
      >
        <PlayerJoinWaitBox
          :host="game.host"
          :playersJoined="game.players?.length"
          :readingTime="game.readingTime"
          :discussionTime="game.discussionTime"
        />
      </div>

      <template
        v-if="elementViewPhases.gameTextsVoteBox.includes(game.phase)"
      >
        <template v-if="tab == 'gameTextsVote'">
          <div
            ref="gameTextsVoteContainer"
            :class="$style.gameTextsVoteContainer"
          >
            <GameTextsVoteBox
              :players="myGroupPlayers ?? []"
              :textVotes="myGroupTextVotes"
              :isHost="canAccessCurrentGroupHostFeatures"
              :canOpenAllGroups="canAccessCurrentGroupHostFeatures"
              :user="user"
              :player="player"
              :updateTextVote="updateTextVote"
              :proceedNext="proceedNext"
              :openAllGroupsGameTextsVotes="openAllGroupsGameTextsVotes"
            />
          </div>
        </template>
      </template>

      <template
        v-if="
          (canUseHostView &&
            elementViewPhases.hostGameTextsBox.includes(game.phase)) ||
          (!canUseHostView &&
            elementViewPhases.playerGameTextsBox.includes(game.phase))
        "
      >
        <template
          v-if="
            ((game.phase == 'DISCUSS' || game.phase == 'DEBRIEF') &&
              tab == 'gameTexts') ||
            (game.phase != 'DISCUSS' && game.phase != 'DEBRIEF')
          "
        >
          <div ref="textsContainer" :class="$style.textsContainer">
            <GameTextsBox
              :texts="game.texts ?? []"
              :discipline="game.discipline"
              :codeLanguage="game.codeLanguage"
              :textTitle="game.textTitle"
              :realTextId="gameStore.displayRealTextId"
              :realTextLabel="gameStore.displayRealTextLabel"
              :openEnlargedGameTexts="openEnlargedGameTexts"
              :imageUrl="game?.imageUrl ?? ''"
              :showImageButton="true"
            />
          </div>
        </template>
      </template>

      <template
        v-if="elementViewPhases.prelimVotesDisplayBox.includes(game.phase)"
      >
        <template
          v-if="
            (game.phase == 'DISCUSS' && tab == 'prelimVotes') ||
            game.phase != 'DISCUSS'
          "
        >
          <div
            ref="prelimVotesDisplayContainer"
            :class="$style.prelimVotesDisplayContainer"
          >
            <PrelimVotesDisplayBox
              :preliminaryVotes="myGroupPrelimVotes"
              :texts="game.texts ?? []"
              :user="user"
              :isHost="canAccessCurrentGroupHostFeatures"
              :canOpenAllGroups="canAccessCurrentGroupHostFeatures"
              :openAllGroupsPrelimVotes="openAllGroupsPrelimVotes"
            />
          </div>
        </template>
      </template>

      <template v-if="!canUseHostView">
        <template v-if="elementViewPhases.topResultsBox.includes(game.phase)">
          <div ref="topResultsContainer" :class="$style.topResultsContainer">
            <TopResultsBox
              :saboteur="game.saboteur"
              :topScorer="myGroupWithSortedPlayersByScore?.topPlayer ?? null"
              :isHost="isHost"
              :player="player"
              :realTextLabel="gameStore.displayRealTextLabel"
            />
          </div>
        </template>
      </template>

      <template v-if="elementViewPhases.leaderboardBox.includes(game.phase)">
        <template v-if="tab == 'leaderboard'">
          <div
            ref="leaderboardContainer"
            :class="[
              $style.leaderboardContainer,
              !scoresReadyForReveal ? $style.leaderboardLoadingContainer : ''
            ]"
          >
            <LeaderboardBox
              v-if="scoresReadyForReveal"
              :sortedPlayers="myGroupWithSortedPlayersByScore?.players ?? []"
              :openAllGroupsLeaderboard="openAllGroupsLeaderboard"
              :isHost="canAccessCurrentGroupHostFeatures"
            />

            <div v-else :class="$style.revealLoaderContainer">
              <img
                :src="canUseHostView ? hostLoaderGif : playerLoaderGif"
                alt="loader-gif"
                class="loader-gif"
              />
            </div>
          </div>
        </template>
      </template>

      <template v-if="elementViewPhases.textVotesBox.includes(game.phase)">
        <template v-if="tab == 'textVotes'">
          <div ref="textVotesContainer" :class="$style.textVotesContainer">
            <TextVotesBox
              :texts="myGroupTextVotes ?? []"
              :players="myGroupPlayers ?? []"
              :realTextId="game.realTextId"
              :openAllGroupsResultTextsVotes="openAllGroupsResultTextsVotes"
              :isHost="canAccessCurrentGroupHostFeatures"
            />
          </div>
        </template>
      </template>

      <template
        v-if="elementViewPhases.saboteurVotesBox.includes(game.phase)"
      >
        <template v-if="tab == 'saboteurVotes'">
          <div
            ref="saboteurVotesContainer"
            :class="$style.saboteurVotesContainer"
          >
            <SaboteurVotesBox
              :saboteurVotes="myGroupWithSortedSaboteurVotes?.votes ?? []"
              :saboteur="game.saboteur"
              :players="game?.players ?? []"
              :openAllGroupsSaboteurVotes="openAllGroupsSaboteurVotes"
              :isHost="canAccessCurrentGroupHostFeatures"
            />
          </div>
        </template>
      </template>

      <template
        v-if="
          (canUseHostView &&
            elementViewPhases.hostGameChatBox.includes(game.phase)) ||
          (!canUseHostView &&
            elementViewPhases.playerGameChatBox.includes(game.phase))
        "
      >
        <template v-if="game.phase !== 'VOTE'">
          <div ref="chatContainer" :class="$style.chatContainer">
            <ChatBox
              :group="myGroup"
              :messages="myGroupMessages ?? []"
              :user="user"
              :sendMessage="sendMessage"
              :openAllGroupsChats="openAllGroupsChats"
              :isHost="canAccessCurrentGroupHostFeatures"
              :readOnly="chatReadOnly"
              :canOpenAllGroups="canAccessCurrentGroupHostFeatures"
            />
          </div>
        </template>
      </template>

      <template
        v-if="elementViewPhases.gamePrelimVoteBox.includes(game.phase)"
      >
        <div ref="prelimVoteContainer" :class="$style.prelimVoteContainer">
          <CastPrelimVoteBoxForPlayer
            v-if="!canUseHostView"
            :texts="game.texts ?? []"
            :enablePreliminaryReason="Boolean(game.enablePreliminaryVoteReason)"
            :submitPrelimVote="submitPrelimVote"
            :hasPrelimVoted="!!player?.prelimVotedAt || prelimJustSubmitted"
          />
          <CastVoteBoxForHost
            v-if="canAccessCurrentGroupHostFeatures"
            :playersCount="focusedPrelimVoteGroup?.totalPlayersCount ?? null"
            :votesCasted="focusedPrelimVoteGroup?.votesCastedCount ?? null"
            :openAllGroupsCastVotes="openAllGroupsCastPrelimVotes"
            :canOpenAllGroups="canAccessCurrentGroupHostFeatures"
            :isPrelim="true"
            :title="$t('prelim-votes')"
          />
        </div>
      </template>

      <template v-if="elementViewPhases.gameVoteBox.includes(game.phase)">
        <div
          ref="voteWithChatContainer"
          :class="$style.voteWithChatContainer"
        >
          <div :class="$style.voteWithChatTop">
            <CastSaboteurVoteBoxForPlayer
              v-if="!canUseHostView"
              :groupPlayers="myGroupPlayers"
              :enableFinalReason="!!game.enableFinalReason"
              :submitVote="submitVote"
              :hasSaboteurVoted="!!player?.saboteurVotedAt || saboteurJustSubmitted"
            />
            <CastVoteBoxForHost
              v-if="canAccessCurrentGroupHostFeatures"
              :playersCount="
                myGroupWithSaboteurVoteCastedPlayers?.totalPlayersCount ?? null
              "
              :votesCasted="
                myGroupWithSaboteurVoteCastedPlayers?.votesCastedCount ?? null
              "
              :openAllGroupsCastVotes="openAllGroupsCastSaboteurVotes"
              :canOpenAllGroups="canAccessCurrentGroupHostFeatures"
              :title="$t('saboteur-votes')"
            />
          </div>
          <div :class="$style.voteWithChatBottom">
            <ChatBox
              :group="myGroup"
              :messages="myGroupMessages ?? []"
              :user="user"
              :sendMessage="sendMessage"
              :openAllGroupsChats="openAllGroupsChats"
              :isHost="isHost"
              :readOnly="chatReadOnly"
            />
          </div>
        </div>
      </template>
    </div>

  <template v-if="winnerModal">
    <GameWinnerModal :gameWinner="gameWinner" :closeModal="closeWinnerModal" />
  </template>

  <template v-if="roleModal && !canUseHostView">
    <PlayerRoleModal
      :isSaboteur="isSaboteur"
      :realTextLabel="gameStore.saboteurRealTextLabel"
      :closeModal="closePlayerRoleModal"
    />
  </template>

  <template v-if="showSaboteursModal">
    <SaboteursModal :groups="saboteurGroupsForViewer" :closeModal="closeSaboteursModal" />
  </template>

  <template v-if="showWinnersModal">
    <WinnersModal :groups="scopedGroupsWithWinner" :closeModal="closeWinnersModal" />
  </template>
</template>

<script>
import { storeToRefs } from "pinia";
import styles from "@/css/PlayGameView.module.css";
import globalStyles from "@/css/global.module.css";
import HostLoaderGif from "@/assets/gifs/host-loader.svg";
import PlayerLoaderGif from "@/assets/gifs/player-loader.svg";
import GameTextsVoteBlackIcon from "@/assets/icons/boxed-star-black.svg";
import GameTextsVoteWhiteIcon from "@/assets/icons/boxed-star-white.svg";
import GameTextsWhiteIcon from "@/assets/icons/sheet-of-paper-white.svg";
import GameTextsBlackIcon from "@/assets/icons/sheet-of-paper.svg";
import SaboteurVotesBlackIcon from "@/assets/icons/audit-black.svg";
import SaboteurVotesWhiteIcon from "@/assets/icons/audit-white.svg";
import VotesBlackIcon from "@/assets/icons/bullet-list-black.svg";
import VotesWhiteIcon from "@/assets/icons/bullet-list-white.svg";
import LeaderboardBlackIcon from "@/assets/icons/leaderboard-black.svg";
import LeaderboardWhiteIcon from "@/assets/icons/leaderboard-white.svg";
import ForwardArrowIcon from "@/assets/icons/forward-arrow.svg";
import { useGameStore } from "@/stores/gameStore";
import { useGameGroupStore } from "@/stores/game/gameGroupStore";
import useDeviceType from "@/utils/useDeviceType";
import useGameViewer from "@/utils/useGameViewer";
import { ELEMENT_VIEW_PHASES } from "@/utils/gameScreenLayout";
import HeaderActionButton from "@/components/buttons/HeaderActionButton.vue";
import TabSelectionButton from "@/components/buttons/TabSelectionButton.vue";
import AllGroupsCastVotesBox from "./AllGroupsCastVotesBox.vue";
import AllGroupsChatsBox from "./AllGroupsChatsBox.vue";
import AllGroupsGameTextsVotesBox from "./AllGroupsGameTextsVotesBox.vue";
import AllGroupsLeaderboardBox from "./AllGroupsLeaderboardBox.vue";
import AllGroupsPrelimVotesBox from "./AllGroupsPrelimVotesBox.vue";
import AllGroupsResultTextsVotesBox from "./AllGroupsResultTextsVotesBox.vue";
import AllGroupsSaboteurVotesBox from "./AllGroupsSaboteurVotesBox.vue";
import CastPrelimVoteBoxForPlayer from "./CastPrelimVoteBoxForPlayer.vue";
import CastSaboteurVoteBoxForPlayer from "./CastSaboteurVoteBoxForPlayer.vue";
import CastVoteBoxForHost from "./CastVoteBoxForHost.vue";
import ChatBox from "./ChatBox.vue";
import EnlargedGameTextsBox from "./EnlargedGameTextsBox.vue";
import FocusGroupBox from "./FocusGroupBox.vue";
import GameTextsBox from "./GameTextsBox.vue";
import GameTextsVoteBox from "./GameTextsVoteBox.vue";
import GameWinnerModal from "./GameWinnerModal.vue";
import HostHeaderBox from "./HostHeaderBox.vue";
import JoinCodeBox from "./JoinCodeBox.vue";
import LeaderboardBox from "./LeaderboardBox.vue";
import PlayerHeaderBox from "./PlayerHeaderBox.vue";
import PlayerJoinWaitBox from "./PlayerJoinWaitBox.vue";
import PlayerRoleModal from "./PlayerRoleModal.vue";
import PlayersBox from "./PlayersBox.vue";
import PlayersCountBox from "./PlayersCountBox.vue";
import PhaseTimerBox from "./PhaseTimerBox.vue";
import PrelimVotesDisplayBox from "./PrelimVotesDisplayBox.vue";
import SaboteursModal from "./SaboteursModal.vue";
import SaboteurVotesBox from "./SaboteurVotesBox.vue";
import TextVotesBox from "./TextVotesBox.vue";
import TopResultsBox from "./TopResultsBox.vue";
import WinnersModal from "./WinnersModal.vue";

const device = useDeviceType();

// Panels that stretch to fill the window below the header and tabs.
const FILL_HEIGHT_REFS = [
  "playersContainer", "playerLobbyWaitContainer", "prelimVotesDisplayContainer", "topResultsContainer",
  "gameTextsVoteContainer", "textsContainer", "leaderboardContainer", "textVotesContainer",
  "saboteurVotesContainer", "chatContainer", "voteWithChatContainer", "prelimVoteContainer",
];

/**
 * The game screen everyone sees during a game: header, timer, tabs, texts,
 * votes, chat, results and their pop-ups. It reads the game from the stores.
 *
 * Used by the live game (PlayGameView) and by the replay (GameReplayView).
 * Each passes in its own actions: the live game sends votes and messages,
 * the replay passes read-only handlers. Layout changes belong here only.
 */
export default {
  name: "GameScreen",
  components: {
    AllGroupsCastVotesBox, AllGroupsChatsBox, AllGroupsGameTextsVotesBox, AllGroupsLeaderboardBox,
    AllGroupsPrelimVotesBox, AllGroupsResultTextsVotesBox, AllGroupsSaboteurVotesBox, CastPrelimVoteBoxForPlayer,
    CastSaboteurVoteBoxForPlayer, CastVoteBoxForHost, ChatBox, EnlargedGameTextsBox, FocusGroupBox, GameTextsBox,
    GameTextsVoteBox, GameWinnerModal, HeaderActionButton, HostHeaderBox, JoinCodeBox, LeaderboardBox,
    PlayerHeaderBox, PlayerJoinWaitBox, PlayerRoleModal, PlayersBox, PlayersCountBox, PhaseTimerBox,
    PrelimVotesDisplayBox, SaboteursModal, SaboteurVotesBox, TabSelectionButton, TextVotesBox, TopResultsBox,
    WinnersModal,
  },
  props: {
    // Selected tab (v-model:tab).
    tab: { type: String, default: "gameTexts" },
    // Pop-ups the parent opens from its own logic (v-model).
    winnerModal: { type: Boolean, default: false },
    roleModal: { type: Boolean, default: false },
    gameWinner: { type: String, default: "" },
    playerRoleReady: { type: Boolean, default: false },
    // Shows the host's Start / Next / End buttons.
    canControlPhase: { type: Boolean, default: false },
    chatReadOnly: { type: Boolean, default: false },
    // Covers the moment between submitting a vote and the stores catching up.
    prelimJustSubmitted: { type: Boolean, default: false },
    saboteurJustSubmitted: { type: Boolean, default: false },
    // False while reveal scores are still loading (shows the loader).
    scoresLoaded: { type: Boolean, default: true },
    pendingModerators: { type: Object, default: () => ({}) },
    sendMessage: { type: Function, required: true },
    updateTextVote: { type: Function, required: true },
    submitPrelimVote: { type: Function, required: true },
    submitVote: { type: Function, required: true },
    proceedNext: { type: Function, required: true },
    // Replays drive the phase timer with their own clock (ms).
    timerNowMs: { type: Number, default: null },
    // Space kept free at the bottom of the window (the replay's playback bar).
    reservedBottom: { type: Number, default: 0 },
  },
  emits: [
    "update:tab", "update:winnerModal", "update:roleModal", "update:pendingModerators", "update:groups",
    "start-game", "continue-game", "end-game", "timer-ended",
  ],
  setup() {
    const gameStore = useGameStore();
    const gameGroupStore = useGameGroupStore();
    const { game, isHost, user, player, isSaboteur } = storeToRefs(gameStore);
    const {
      groups,
      groupsWithWinner,
      groupsWithPlayers,
      groupsWithMessages,
      groupsWithPrelimVotes,
      groupsWithTextVotes,
      groupsWithSortedPlayersByScore,
      groupsWithSortedSaboteurVotesWithPlayers,
      groupsWithSaboteurVoteCastedPlayers,
      groupsWithPrelimVoteCastedPlayers,
      myGroup,
      myGroupPlayers,
      myGroupMessages,
      myGroupPrelimVotes,
      myGroupTextVotes,
      myGroupWithSortedPlayersByScore,
      myGroupWithSortedSaboteurVotes,
      myGroupWithSaboteurVoteCastedPlayers,
    } = storeToRefs(gameGroupStore);

    return {
      gameStore,
      game,
      isHost,
      user,
      player,
      isSaboteur,
      groups,
      groupsWithWinner,
      groupsWithPlayers,
      groupsWithMessages,
      groupsWithPrelimVotes,
      groupsWithTextVotes,
      groupsWithSortedPlayersByScore,
      groupsWithSortedSaboteurVotesWithPlayers,
      groupsWithSaboteurVoteCastedPlayers,
      groupsWithPrelimVoteCastedPlayers,
      myGroup,
      myGroupPlayers,
      myGroupMessages,
      myGroupPrelimVotes,
      myGroupTextVotes,
      myGroupWithSortedPlayersByScore,
      myGroupWithSortedSaboteurVotes,
      myGroupWithSaboteurVoteCastedPlayers,
      ...useGameViewer(),
      elementViewPhases: ELEMENT_VIEW_PHASES,
    };
  },
  data() {
    return {
      hostLoaderGif: HostLoaderGif,
      playerLoaderGif: PlayerLoaderGif,
      forwardArrowIcon: ForwardArrowIcon,
      gameTextsVoteWhiteIcon: GameTextsVoteWhiteIcon,
      gameTextsVoteBlackIcon: GameTextsVoteBlackIcon,
      gameTextsWhiteIcon: GameTextsWhiteIcon,
      gameTextsBlackIcon: GameTextsBlackIcon,
      saboteurVotesBlackIcon: SaboteurVotesBlackIcon,
      saboteurVotesWhiteIcon: SaboteurVotesWhiteIcon,
      votesBlackIcon: VotesBlackIcon,
      votesWhiteIcon: VotesWhiteIcon,
      leaderboardBlackIcon: LeaderboardBlackIcon,
      leaderboardWhiteIcon: LeaderboardWhiteIcon,
      // Full-screen panels opened from inside the screen.
      showEnlargedGameTexts: false,
      showAllGroupsChats: false,
      showAllGroupsPrelimVotes: false,
      showAllGroupsGameTextsVotes: false,
      showAllGroupsResultTextsVotes: false,
      showAllGroupsLeaderboard: false,
      showAllGroupsSaboteurVotes: false,
      showAllGroupsCastSaboteurVotes: false,
      showAllGroupsCastPrelimVotes: false,
      showSaboteursModal: false,
      showWinnersModal: false,
    };
  },
  computed: {
    $style() {
      return styles;
    },
    $globalStyle() {
      return globalStyles;
    },
    isMobile() {
      return device.isMobile.value;
    },
    isTablet() {
      return device.isTablet.value;
    },
    scopedGroupsWithPlayers() {
      return this.scopeToViewer(this.groupsWithPlayers);
    },
    scopedGroupsWithMessages() {
      return this.scopeToViewer(this.groupsWithMessages);
    },
    scopedGroupsWithPrelimVotes() {
      return this.scopeToViewer(this.groupsWithPrelimVotes);
    },
    scopedGroupsWithTextVotes() {
      return this.scopeToViewer(this.groupsWithTextVotes);
    },
    scopedGroupsWithSortedPlayersByScore() {
      return this.scopeToViewer(this.groupsWithSortedPlayersByScore);
    },
    scopedGroupsWithSortedSaboteurVotesWithPlayers() {
      return this.scopeToViewer(this.groupsWithSortedSaboteurVotesWithPlayers);
    },
    scopedGroupsWithWinner() {
      return this.scopeToViewer(this.groupsWithWinner);
    },
    scopedGroupsWithSaboteurVoteCastedPlayers() {
      return this.scopeToViewer(this.groupsWithSaboteurVoteCastedPlayers);
    },
    scopedGroupsWithPrelimVoteCastedPlayers() {
      return this.scopeToViewer(this.groupsWithPrelimVoteCastedPlayers);
    },
    scoresReadyForReveal() {
      if (!["REVEAL", "DEBRIEF"].includes(this.game?.phase)) return true;
      const players = this.myGroupWithSortedPlayersByScore?.players ?? [];
      return this.scoresLoaded && players.length > 0;
    },
  },
  watch: {
    "game.phase"() {
      this.closeOverlays();
      this.recalculateHeightAfterRender();
    },
    "user.id"() {
      this.closeOverlays();
    },
    tab() {
      this.recalculateHeightAfterRender();
    },
    reservedBottom() {
      this.calculateDynamicHeight();
    },
  },
  mounted() {
    this.recalculateHeightAfterRender();
    window.addEventListener("resize", this.calculateDynamicHeight);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.calculateDynamicHeight);
  },
  methods: {
    calculateDynamicHeight() {
      this.$nextTick(() => {
        const headerHeight = this.$refs.headerContainer?.offsetHeight || 0;
        const tabsContainerHeight = this.$refs.tabsContainer?.offsetHeight || 0;
        const availableHeight = window.innerHeight - this.reservedBottom;
        const remainingHeight =
          device.width.value <= 425
            ? 420
            : device.height.value >= 740
            ? availableHeight - headerHeight - tabsContainerHeight - 17
            : availableHeight - 17;

        for (const ref of FILL_HEIGHT_REFS) {
          if (this.$refs[ref]) this.$refs[ref].style.height = `${remainingHeight}px`;
        }
      });
    },
    recalculateHeightAfterRender() {
      this.$nextTick(() => {
        this.calculateDynamicHeight();
        setTimeout(() => this.calculateDynamicHeight(), 100);
        setTimeout(() => this.calculateDynamicHeight(), 300);
      });
    },
    selectTab(label) {
      this.$emit("update:tab", label);
    },
    startGame() {
      this.$emit("start-game");
    },
    continueGame() {
      this.$emit("continue-game");
    },
    endGame() {
      this.$emit("end-game");
    },
    onTimerEnd() {
      this.$emit("timer-ended");
    },
    // A new phase or a different viewer starts with every panel closed.
    closeOverlays() {
      this.showEnlargedGameTexts = false;
      this.showAllGroupsChats = false;
      this.showAllGroupsPrelimVotes = false;
      this.showAllGroupsGameTextsVotes = false;
      this.showAllGroupsResultTextsVotes = false;
      this.showAllGroupsLeaderboard = false;
      this.showAllGroupsSaboteurVotes = false;
      this.showAllGroupsCastSaboteurVotes = false;
      this.showAllGroupsCastPrelimVotes = false;
    },
    openEnlargedGameTexts() {
      this.showEnlargedGameTexts = true;
    },
    closeEnlargedGameTexts() {
      this.showEnlargedGameTexts = false;
    },
    openAllGroupsChats() {
      this.showAllGroupsChats = true;
    },
    closeAllGroupsChats() {
      this.showAllGroupsChats = false;
    },
    openAllGroupsPrelimVotes() {
      this.showAllGroupsPrelimVotes = true;
    },
    closeAllGroupsPrelimVotes() {
      this.showAllGroupsPrelimVotes = false;
    },
    openAllGroupsGameTextsVotes() {
      this.showAllGroupsGameTextsVotes = true;
    },
    closeAllGroupsGameTextsVotes() {
      this.showAllGroupsGameTextsVotes = false;
    },
    openAllGroupsResultTextsVotes() {
      this.showAllGroupsResultTextsVotes = true;
    },
    closeAllGroupsResultTextsVotes() {
      this.showAllGroupsResultTextsVotes = false;
    },
    openAllGroupsLeaderboard() {
      this.showAllGroupsLeaderboard = true;
    },
    closeAllGroupsLeaderboard() {
      this.showAllGroupsLeaderboard = false;
    },
    openAllGroupsSaboteurVotes() {
      this.showAllGroupsSaboteurVotes = true;
    },
    closeAllGroupsSaboteurVotes() {
      this.showAllGroupsSaboteurVotes = false;
    },
    openAllGroupsCastSaboteurVotes() {
      this.showAllGroupsCastSaboteurVotes = true;
    },
    closeAllGroupsCastSaboteurVotes() {
      this.showAllGroupsCastSaboteurVotes = false;
    },
    openAllGroupsCastPrelimVotes() {
      this.showAllGroupsCastPrelimVotes = true;
    },
    closeAllGroupsCastPrelimVotes() {
      this.showAllGroupsCastPrelimVotes = false;
    },
    openSaboteursModal() {
      this.showSaboteursModal = true;
    },
    closeSaboteursModal() {
      this.showSaboteursModal = false;
    },
    openWinnersModal() {
      this.showWinnersModal = true;
    },
    closeWinnersModal() {
      this.showWinnersModal = false;
    },
    openPlayerRoleModal() {
      if (!this.playerRoleReady) return;
      this.$emit("update:roleModal", true);
    },
    closePlayerRoleModal() {
      this.$emit("update:roleModal", false);
    },
    closeWinnerModal() {
      this.$emit("update:winnerModal", false);
    },
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}
</style>
